import { Directive, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControlName } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Displays ISO date values as dd/mm/yyyy or dd/mm/yyyy HH:mm on text inputs.
 * Not compatible with type="datetime-local" — use only with type="text".
 */
@Directive({
  selector: '[sharedDateFormat]',
  standalone: false,
})
export class DateFormatDirective implements OnInit, OnDestroy {
  private sub?: Subscription;
  private updating = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private controlName: FormControlName,
  ) {}

  ngOnInit() {
    const ctrl = this.controlName.control;

    if (ctrl?.value && this.isValidISO(ctrl.value)) {
      this.el.nativeElement.value = this.toDisplay(ctrl.value);
    }

    this.sub = ctrl?.valueChanges.subscribe((v) => {
      // Only update display when not focused and value is valid ISO
      if (!this.updating && !this.isFocused() && v && this.isValidISO(v)) {
        this.el.nativeElement.value = this.toDisplay(v);
      }
    });
  }

  @HostListener('blur')
  onBlur() {
    const iso = this.toISO(this.el.nativeElement.value);
    if (iso) {
      this.updating = true;
      this.controlName.control?.setValue(iso);
      this.updating = false;
      this.el.nativeElement.value = this.toDisplay(iso);
    }
  }

  private isFocused(): boolean {
    return document.activeElement === this.el.nativeElement;
  }

  private isValidISO(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?/.test(value);
  }

  private toDisplay(iso: string): string {
    try {
      const [datePart, timePart] = iso.split('T');
      const [y, m, d] = datePart.split('-');
      if (!y || !m || !d) return iso;
      const time = timePart?.substring(0, 5) ?? '';
      return time ? `${d}/${m}/${y} ${time}` : `${d}/${m}/${y}`;
    } catch {
      return iso;
    }
  }

  private toISO(display: string): string | null {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (!match) return null;
    const [, d, m, y, h, min] = match;
    return h ? `${y}-${m}-${d}T${h}:${min}` : `${y}-${m}-${d}`;
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
